import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Bot, Clock, Github, Film, Loader2 } from "lucide-react";
import { getError, getAnalysis } from "../api/client";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import ReplayPlayer from "../components/ReplayPlayer";

function ErrorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingPR, setCreatingPR] = useState(false);
  const [prMessage, setPrMessage] = useState(null);
  const [showReplay, setShowReplay] = useState(false);
  const [replayData, setReplayData] = useState(null);
  const [loadingReplay, setLoadingReplay] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [errorData, analysisData] = await Promise.all([
        getError(id),
        getAnalysis(id),
      ]);
      setError(errorData);
      setAnalysis(analysisData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchReplay = async () => {
    try {
      setLoadingReplay(true);
      const response = await api.get(`/api/v1/errors/${id}/replay`);
      setReplayData(response.data.replay_data);
      setShowReplay(true);
    } catch (error) {
      console.error("Failed to fetch replay:", error);
      alert("No session replay available for this error");
    } finally {
      setLoadingReplay(false);
    }
  };

  const handleCreatePR = async () => {
    try {
      setCreatingPR(true);
      setPrMessage(null);

      const response = await api.post(`/api/v1/github/errors/${id}/create-pr`);

      if (response.data.success) {
        setPrMessage({
          type: "success",
          text: `PR created successfully! View it here: ${response.data.pr_url || `https://github.com/${user?.github_repo || "your-repo"}/pulls`}`,
          url:
            response.data.pr_url ||
            `https://github.com/${user?.github_repo || "your-repo"}/pulls`,
        });
      } else {
        setPrMessage({
          type: "error",
          text: response.data.message || "Failed to create PR",
        });
      }
    } catch (error) {
      console.error("Error creating PR:", error);
      setPrMessage({
        type: "error",
        text:
          error.response?.data?.detail ||
          "Failed to create PR. Please try again.",
      });
    } finally {
      setCreatingPR(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  if (!error) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Error not found</p>
        <Link
          to="/errors"
          className="text-cyan-400 hover:text-cyan-300 transition-colors duration-200"
        >
          Back to Errors
        </Link>
      </div>
    );
  }

  const getSeverityColor = (severity) => {
    const colors = {
      critical: "from-red-500 to-red-600 border-red-500/30 text-red-400",
      error:
        "from-orange-500 to-orange-600 border-orange-500/30 text-orange-400",
      warning:
        "from-yellow-500 to-yellow-600 border-yellow-500/30 text-yellow-400",
      info: "from-blue-500 to-blue-600 border-blue-500/30 text-blue-400",
    };
    return colors[severity] || colors.error;
  };

  return (
    <div>
      <div className="mb-6">
        <Link
          to="/errors"
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Errors
        </Link>

        <div className="flex items-center gap-3 flex-wrap">
          <span
            className={`px-3 py-1 text-sm font-medium rounded-lg border bg-gradient-to-br ${getSeverityColor(error.severity)}`}
          >
            {error.severity}
          </span>
          <span className="text-sm text-gray-400">
            {new Date(error.created_at).toLocaleString()}
          </span>
          {error.has_ai_analysis && (
            <span className="flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-lg bg-gradient-to-r from-cyan-500/20 to-blue-600/20 text-cyan-400 border border-cyan-400/20">
              <Bot className="w-4 h-4" />
              AI Analyzed
            </span>
          )}
          {error.has_session_replay && (
            <span className="flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-600/20 text-purple-400 border border-purple-400/20">
              <Film className="w-4 h-4" />
              Replay Available
            </span>
          )}
        </div>

        <h1 className="text-2xl font-bold text-white mt-2">
          {error.type}: {error.message}
        </h1>

        {error.url && (
          <p className="text-sm text-gray-400 mt-1">URL: {error.url}</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {error.stack_trace && (
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm border border-gray-800/50 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-3">Stack Trace</h3>
              <pre className="bg-gray-950/50 text-gray-300 rounded-lg p-4 text-sm overflow-x-auto border border-gray-800/50">
                {error.stack_trace}
              </pre>
            </div>
          )}

          {error.metadata && Object.keys(error.metadata).length > 0 && (
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm border border-gray-800/50 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-3">Metadata</h3>
              <pre className="bg-gray-950/50 text-gray-300 rounded-lg p-4 text-sm overflow-x-auto border border-gray-800/50">
                {JSON.stringify(error.metadata, null, 2)}
              </pre>
            </div>
          )}

          {/* Replay Player */}
          {showReplay && replayData && (
            <ReplayPlayer
              replayData={replayData}
              onClose={() => setShowReplay(false)}
            />
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm border border-gray-800/50 rounded-xl p-6">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Bot className="w-5 h-5 text-cyan-400" />
              AI Analysis
            </h3>

            {analysis &&
            analysis.status === "completed" &&
            analysis.root_cause ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Root Cause</p>
                  <p className="text-gray-200">{analysis.root_cause}</p>
                </div>

                {analysis.suggested_fix && (
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Suggested Fix</p>
                    <pre className="bg-gray-950/50 text-cyan-300 rounded-lg p-3 text-sm overflow-x-auto whitespace-pre-wrap border border-cyan-500/20">
                      {analysis.suggested_fix}
                    </pre>
                  </div>
                )}

                {analysis.confidence !== undefined &&
                  analysis.confidence !== null && (
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Confidence</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-800/50 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full h-2 transition-all duration-1000"
                            style={{
                              width: `${Math.min(analysis.confidence * 100, 100)}%`,
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium text-cyan-400">
                          {Math.round(analysis.confidence * 100)}%
                        </span>
                      </div>
                    </div>
                  )}

                {/* View Replay Button */}
                {error.has_session_replay && (
                  <div>
                    <button
                      onClick={fetchReplay}
                      disabled={loadingReplay}
                      className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loadingReplay ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Film className="w-4 h-4" />
                      )}
                      {loadingReplay
                        ? "Loading Replay..."
                        : "View Session Replay"}
                    </button>
                  </div>
                )}

                {/* Create PR Button */}
                {analysis.suggested_fix && (
                  <div>
                    <button
                      onClick={handleCreatePR}
                      disabled={creatingPR}
                      className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Github className="w-4 h-4" />
                      {creatingPR
                        ? "Creating PR..."
                        : "Create GitHub PR with Fix"}
                    </button>

                    {prMessage && (
                      <div
                        className={`mt-3 p-3 rounded-lg text-sm ${
                          prMessage.type === "success"
                            ? "bg-green-500/10 border border-green-500/20 text-green-400"
                            : "bg-red-500/10 border border-red-500/20 text-red-400"
                        }`}
                      >
                        {prMessage.text}
                        {prMessage.url && (
                          <a
                            href={prMessage.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-cyan-400 hover:underline mt-1"
                          >
                            View PR →
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <Clock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400">
                  {analysis?.status === "pending"
                    ? "Analysis pending..."
                    : "No analysis available"}
                </p>
                <p className="text-sm text-gray-500">
                  {analysis?.status === "pending"
                    ? "AI will analyze this error shortly"
                    : "This error has not been analyzed yet"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ErrorDetail;
